import { useEffect, useId, useRef, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { Overlay } from "../../Components/Overlay"
import type { ILogbookAppRecordingClockStatus } from "../../Types/Generated/LogbookApp/AppStatus"

interface RecordingClockWarningProps {
  status?: ILogbookAppRecordingClockStatus;
}

export function RecordingClockWarning({ status }: RecordingClockWarningProps) {
  if (!status || (!status.blocked && !status.systemFailed && !status.gpsFailed)) {
    return null;
  }
  const message = status.blocked ? "paused" : status.systemFailed ? "system" : "gps";
  return <ClockDialog key={message} message={message} />;
}

function ClockDialog({ message }: { message: "paused" | "system" | "gps" }) {
  const t = GetApp().getT("RecordingClockWarning");
  const [dismissed, setDismissed] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const descriptionId = useId();
  useEffect(() => {
    if (dismissed) return;
    const previous = document.activeElement;
    button.current?.focus();
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [dismissed]);
  if (dismissed) return null;

  return (
    <Overlay containerClassName="RecordingClockWarning-layer" className="RecordingClockWarning overlay-centered"
      style={message === "paused" ? "alarm" : "warning"} title={t("title")} hideClose
    >
      <div className="Overlay-body" role="alertdialog" aria-modal="true" aria-label={t("title")}
        aria-describedby={descriptionId} onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            setDismissed(true);
          } else if (event.key === "Tab") {
            event.preventDefault();
            button.current?.focus();
          }
        }}
      >
        <Block>
          <BlockRow><BlockField><span id={descriptionId}>{t(message)}</span></BlockField></BlockRow>
          <BlockRow actions>
            <BlockField>
              <button ref={button} className="button default" onClick={() => setDismissed(true)}>{t("acknowledge")}</button>
            </BlockField>
          </BlockRow>
        </Block>
      </div>
    </Overlay>
  );
}
